/**
 * Country Context
 * Manages selected country and exposes derived settings:
 * currency, payment methods, date format.
 *
 * Each country belongs to a market (utils/market.js): US → global,
 * India → india. The active country is always one of the current market's
 * countries — a saved country from another market is replaced by this
 * market's default and written back, so formatters (which read localStorage)
 * agree. `countries` lists every country with its market; the header picker
 * decides which are selectable and switches market when needed.
 */

import React, { createContext, useState, useContext, useCallback } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryConfig } from '../config/countryConfig';
import { MARKET, marketDefaults, countryMarket } from '@utils/market';

export const CountryContext = createContext(null);

const MARKET_COUNTRIES = (marketDefaults(MARKET).countries || [DEFAULT_COUNTRY]).filter(code => COUNTRIES[code]);
const ALL_COUNTRIES = Object.values(COUNTRIES).map(c => ({ ...c, market: countryMarket(c.code) }));

const initialCountry = () => {
  let saved = null;
  try { saved = localStorage.getItem('vowsera-country'); } catch { /* storage blocked */ }
  const code = saved && MARKET_COUNTRIES.includes(saved) ? saved : (MARKET_COUNTRIES[0] || DEFAULT_COUNTRY);
  try { localStorage.setItem('vowsera-country', code); } catch { /* storage blocked */ }
  return code;
};

export const CountryProvider = ({ children }) => {
  const [countryCode, setCountryCode] = useState(initialCountry);

  const config = getCountryConfig(countryCode);

  /** Change country within the current market (a market switch reloads instead). */
  const changeCountry = useCallback((code) => {
    if (COUNTRIES[code] && MARKET_COUNTRIES.includes(code)) {
      setCountryCode(code);
      try { localStorage.setItem('vowsera-country', code); } catch { /* storage blocked */ }
    }
  }, []);

  return (
    <CountryContext.Provider
      value={{
        countryCode,
        countryName: config.name,
        flag: config.flag,
        currency: config.currency,
        currencySymbol: config.currencySymbol,
        currencyName: config.currencyName,
        dateFormat: config.dateFormat,
        locale: config.locale,
        paymentMethods: config.paymentMethods,
        supportedBadges: config.supportedBadges || [],
        country: config,
        countries: ALL_COUNTRIES,
        changeCountry,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error('useCountry must be used within CountryProvider');
  return ctx;
};

export default CountryContext;
